import { useState } from 'react';
import { Box, Flex } from '@chakra-ui/react';
import { KulaTextLogo } from '@/assets/icons/colored';
import { Text, Button, Input } from '@kula-ai/components';

// OAuth params Claude appends to GET /oauth/authorize — carried verbatim into the submit so the
// front door can mint the auth_req. The page adds only the chosen workspace.
const OAUTH_KEYS = [
  'response_type',
  'client_id',
  'redirect_uri',
  'code_challenge',
  'code_challenge_method',
  'state',
  'scope',
  'resource',
];

// "acme.kula.ai", "https://acme.kula.ai/x" or "acme" → "acme": the backend resolves the workspace
// by its subdomain (Shard.find_cached_domain), so send only the first label.
const toWorkspace = (value: string) =>
  value
    .trim()
    .replace(/^https?:\/\//, '')
    .split(/[./]/)[0]
    .toLowerCase();

export const McpAuthorize = () => {
  const [accountUrl, setAccountUrl] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const onContinue = async () => {
    const workspace = toWorkspace(accountUrl);
    if (!workspace) {
      setError('Enter your Kula account URL.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const params = new URLSearchParams(window.location.search);
      const body = new URLSearchParams();
      OAUTH_KEYS.forEach((key) => {
        const value = params.get(key);
        if (value !== null) body.set(key, value);
      });
      body.set('workspace', workspace);

      // Core AS endpoint on oauth.<domain> under /oauth/v1 — a raw POST, not the /api/v1 http instance.
      const res = await fetch('/oauth/v1/authorize/submit', {
        method: 'POST',
        body,
      });
      const data = await res.json();
      if (data.redirect_url) {
        window.location.href = data.redirect_url;
        return;
      }
      setError(
        data.error === 'invalid_workspace'
          ? "We couldn't find that workspace. Double-check your account URL."
          : 'Something went wrong. Please try again.'
      );
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Flex
      position="fixed"
      inset="0"
      align="center"
      justify="center"
      bg="white"
      p="16px"
    >
      <Box
        w="100%"
        maxW="480px"
        border="1px solid"
        borderColor="border-color"
        borderRadius="12px"
        p={{ base: '24px', md: '32px' }}
      >
        <Box mb="24px">
          <KulaTextLogo width={96} height={30} />
        </Box>
        <Text variant="h4" color="heading" className="mb-1">
          Connect to Kula MCP
        </Text>
        <Text variant="p" color="muted" className="mb-5">
          Enter your Kula account URL to authorize this MCP client
        </Text>
        <Input
          value={accountUrl}
          onChange={(e) => setAccountUrl(e.target.value)}
          placeholder="acme.kula.ai"
          error={!!error}
          onKeyDown={(e) => e.key === 'Enter' && onContinue()}
        />
        {error && (
          <Text variant="caption" color="error" className="mt-1">
            {error}
          </Text>
        )}
        <Button
          variant="primary"
          className="mt-4 w-full"
          isLoading={submitting}
          onClick={onContinue}
        >
          Continue
        </Button>
      </Box>
    </Flex>
  );
};
